import { useEffect, useState } from "react";
import type { FC, ReactNode } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { SolutionModal } from "features";

import type { AssignmentPracticeFragment } from "api/generated";

import { useAppDispatch } from "store/hooks";
import { setIsSoundMuted } from "store/slices/user";

import { eventEmitter, Events } from "utils/events/EventEmitter";

import IconCheck from "images/icons/check.svg";
import IconCross from "images/icons/close.svg";
import { gtmTaskEvent } from "../../utils/gtmEvents";

interface Props {
  addIcon?: boolean;
  assignment: AssignmentPracticeFragment | undefined | null;
  children: ReactNode;
  index: number;
  subject?: string | null;
  timeToLearn?: number;
  tipDisabled?: boolean;
  type: "success" | "warning" | "danger" | undefined;
  userAssignmentId: string;
  toggleModals?: (index: number | null) => void;
}

const AnswerTutoring: FC<Props> = ({
  assignment,
  children,
  index,
  subject,
  type,
  userAssignmentId,
  addIcon = false,
  toggleModals,
}) => {
  if (assignment === undefined || assignment === null) return null;

  const dispatch = useAppDispatch();
  const [isModalVisible, setModalVisibility] = useState(false);
  const { t } = useTranslation(["exercise"]);

  useEffect(() => {
    eventEmitter.dispatch(Events.ONBOARDING_UPDATE);
  }, []);

  const getIcon = () =>
    type === "success" ? (
      <IconCheck className={"text-success ms-4px"} />
    ) : (
      <IconCross className={"text-danger ms-4px"} />
    );

  const toggleModal = () => {
    if (toggleModals !== undefined) {
      toggleModals(isModalVisible ? null : index);
    }
    setModalVisibility(visibility => {
      dispatch(setIsSoundMuted(!visibility));
      return !visibility;
    });
  };

  useEffect(() => {
    if (isModalVisible) {
      gtmTaskEvent("task-analysis", {
        id: assignment.id,
        subject,
      });
    }
  }, [isModalVisible]);

  return (
    <>
      <Alert variant={type ?? "danger"} className="flex-grow-1">
        <Row className="flex-nowrap">
          <Col xs={"auto"} className={"d-flex align-items-center my-auto flex-grow-1 flex-shrink-1"}>
            {children}
            {addIcon && getIcon()}
          </Col>
          <Col xs={"auto"} className={"ms-auto my-auto d-flex align-items-center"}>
            <Button variant={"select"} className={"btn-shadow ms-4px btn-smaller"} onClick={toggleModal} id="analysis">
              {t("btn.analysisMobile")}
            </Button>
          </Col>
        </Row>
      </Alert>

      <SolutionModal
        isShown={isModalVisible}
        assignment={assignment}
        questionIndex={index}
        answerType={type ?? "danger"}
        toggleModal={toggleModal}
        userAssignmentId={userAssignmentId}
      >
        {children}
      </SolutionModal>
    </>
  );
};

export default AnswerTutoring;
